import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './LandingPage.css';
import trackingService from '../services/trackingService';

// =============================================================================
// CONFIGURAÇÃO DE PROMOÇÕES
// Para ativar uma promoção, altere PROMO_CONFIG abaixo
// =============================================================================
const PROMO_CONFIG = {
  enabled: false, // ← Altere para true para ativar promoção
  name: 'BLACK FRIDAY',
  startDate: new Date(2025, 10, 27), // 27 de novembro 2025 (mês 0-indexed)
  endDate: new Date(2025, 10, 30, 23, 59, 59), // 30 de novembro 2025 até 23:59
  trialDays: 60,
  discountPercent: 50,
  bannerImage: '/img/blackfriday.png',
  bannerAlt: 'Black Friday - 60 dias grátis + 50% OFF no plano anual'
};

const LandingPage = () => {
  // Estado para controlar popup de promoção
  const [showPromoPopup, setShowPromoPopup] = useState(false);

  // Rastrear visualização da landing page
  useEffect(() => {
    trackingService.trackLandingPageView();
  }, []);

  useEffect(() => {
    // Verificar se promoção está ativa e se popup já foi fechado
    if (!PROMO_CONFIG.enabled) return;
    
    const popupClosed = sessionStorage.getItem('promoPopupClosed');
    const now = new Date();
    const isPromoPeriod = now >= PROMO_CONFIG.startDate && now <= PROMO_CONFIG.endDate;
    
    if (isPromoPeriod && !popupClosed) {
      const timer = setTimeout(() => {
        setShowPromoPopup(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  const closePromoPopup = () => {
    setShowPromoPopup(false);
    sessionStorage.setItem('promoPopupClosed', 'true');
  };

  // Função para scroll suave até a seção de preços
  const scrollToPrecos = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    trackingService.trackViewPlans(); // Rastrear clique em "Ver Planos"
    const precosSection = document.getElementById('pricing');
    if (precosSection) {
      precosSection.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  // Função para scroll suave até a seção de segurança
  const scrollToSeguranca = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const segurancaSection = document.getElementById('security');
    if (segurancaSection) {
      segurancaSection.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  // Estilos inline para evitar problemas de CSS import
  const headerStyle = {
    backgroundColor: '#ffffff',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    zIndex: 1000,
    position: 'sticky' as const,
    top: 0
  };

  const heroStyle = {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    minHeight: '80vh'
  };

  return (
    <div className="landing-page" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' }}>
      {/* Header */}
      <header className="header sticky-top" style={headerStyle}>
        <div className="container">
          <nav className="navbar navbar-expand-lg navbar-light">
            <Link className="navbar-brand d-flex align-items-center" to="/">
              <img 
                src="/img/logo-icone.png" 
                alt="Sophya" 
                className="me-2"
                style={{ height: '40px' }}
              />
              <span className="fw-bold text-primary fs-4">Sophya</span>
            </Link>
            
            <button 
              className="navbar-toggler" 
              type="button" 
              data-bs-toggle="collapse" 
              data-bs-target="#navbarNav"
            >
              <span className="navbar-toggler-icon"></span>
            </button>
            
            <div className="collapse navbar-collapse" id="navbarNav">
              <ul className="navbar-nav ms-auto">
                <li className="nav-item">
                  <a className="nav-link" href="#features">Funcionalidades</a>
                </li>
                <li className="nav-item">
                  <a className="nav-link" href="#benefits">Benefícios</a>
                </li>
                <li className="nav-item">
                  <a className="nav-link" href="#security" onClick={scrollToSeguranca}>Segurança</a>
                </li>
                <li className="nav-item">
                  <a className="nav-link" href="#pricing">Preços</a>
                </li>
                <li className="nav-item">
                  <Link className="nav-link" to="/login">Login</Link>
                </li>
                <li className="nav-item">
                  <a 
                    className="btn btn-primary ms-2" 
                    href="#pricing"
                    onClick={scrollToPrecos}
                  >
                    Cadastrar
                  </a>
                </li>
              </ul>
            </div>
          </nav>
        </div>
      </header>

      {/* Banner de Promoção - Ativado via PROMO_CONFIG */}
      {PROMO_CONFIG.enabled && (
        <div className="blackfriday-banner">
          <a href="#pricing" onClick={scrollToPrecos}>
            <img 
              src={PROMO_CONFIG.bannerImage}
              alt={PROMO_CONFIG.bannerAlt}
              className="blackfriday-banner-img"
            />
          </a>
        </div>
      )}

      {/* Hero Section */}
      <section className="hero-section py-5" style={heroStyle}>
        <div className="container">
          <div className="row align-items-center min-vh-50">
            <div className="col-lg-6">
              <h1 className="display-4 fw-bold text-white mb-4">
                Gerencie seu consultório de psicologia com facilidade
              </h1>
              <p className="lead text-white mb-4" style={{ opacity: 0.9 }}>
                Sistema completo para gerenciamento de consultórios de psicologia. 
                Cadastre pacientes, agende consultas, registre prontuários e acompanhe relatórios financeiros.
              </p>
              <div className="d-flex gap-3 flex-wrap">
                <a 
                  href="#pricing" 
                  className="btn btn-lg px-4"
                  onClick={scrollToPrecos}
                  style={{ background: '#ffffff', color: '#667eea', fontWeight: 600, border: 'none', boxShadow: '0 4px 15px rgba(0,0,0,0.2)' }}
                >
                  Ver Planos
                </a>
                <Link to="/login" className="btn btn-lg px-4" style={{ background: 'transparent', color: '#ffffff', border: '2px solid #ffffff', fontWeight: 600 }}>
                  Fazer Login
                </Link>
              </div>
              <div className="mt-4 text-white" style={{ opacity: 0.9 }}>
                <small>✅ Teste grátis por 30 dias • Sem compromisso • Cancele quando quiser</small>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="text-center">
                <div 
                  className="dashboard-preview rounded shadow" 
                  style={{ 
                    height: '350px',
                    maxWidth: '600px',
                    margin: '0 auto',
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    borderRadius: '20px',
                    padding: '10px',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <img 
                    src="/img/dashboard-landing-page.png" 
                    alt="Dashboard Sophya - Interface moderna e intuitiva" 
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: '100%',
                      width: 'auto',
                      height: 'auto',
                      objectFit: 'contain',
                      borderRadius: '15px',
                      transition: 'transform 0.3s ease',
                      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)'
                    }}
                    onError={(e) => {
                      // Fallback se a imagem não carregar
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent) {
                        parent.innerHTML = `
                          <div class="d-flex align-items-center justify-content-center h-100 text-white text-center">
                            <div>
                              <div style="font-size: 4rem; margin-bottom: 1rem;">📊</div>
                              <h4>Dashboard Sophya</h4>
                              <p class="mb-0" style="opacity: 0.8;">Interface moderna e intuitiva</p>
                            </div>
                          </div>
                        `;
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold section-title">Funcionalidades Completas</h2>
            <p className="text-muted">Tudo que você precisa para gerenciar seu consultório</p>
          </div>
          
          <div className="row g-4">
            {/* Card 1: Gestão de Pacientes e Prontuários */}
            <div className="col-lg-4 col-md-6">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body text-center p-4">
                  <div className="mb-3">
                    <i className="fas fa-users feature-icon"></i>
                  </div>
                  <h5 className="card-title">Gestão de Pacientes e Prontuários</h5>
                  <p className="card-text text-muted">
                    Cadastro completo de pacientes, prontuários digitais com histórico de consultas, queixas e sintomas. Inclui <strong>OCR para transcrição de documentos manuscritos</strong>.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Card 2: Agendamento e Confirmações */}
            <div className="col-lg-4 col-md-6">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body text-center p-4">
                  <div className="mb-3">
                    <i className="fas fa-calendar-check feature-icon"></i>
                  </div>
                  <h5 className="card-title">Agendamento Inteligente</h5>
                  <p className="card-text text-muted">
                    Agenda visual com navegação por dia, semana e mês. <strong>Confirmação automática de consultas</strong> por e-mail e lembretes para reduzir faltas.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Card 3: Assistente com IA */}
            <div className="col-lg-4 col-md-6">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body text-center p-4">
                  <div className="mb-3">
                    <i className="fas fa-robot feature-icon"></i>
                  </div>
                  <h5 className="card-title">Assistente com Inteligência Artificial</h5>
                  <p className="card-text text-muted">
                    Análise de pacientes com IA para apoio nas decisões clínicas. Sugestões baseadas no histórico e padrões identificados.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Card 4: Recursos Terapêuticos */}
            <div className="col-lg-4 col-md-6">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body text-center p-4">
                  <div className="mb-3">
                    <i className="fas fa-gamepad feature-icon"></i>
                  </div>
                  <h5 className="card-title">Recursos Terapêuticos</h5>
                  <p className="card-text text-muted">
                    <strong>Jogos educativos</strong> para atendimento infantil e terapia lúdica. <strong>Buscador de livros</strong> para referências bibliográficas.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Card 5: Financeiro e Relatórios */}
            <div className="col-lg-4 col-md-6">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body text-center p-4">
                  <div className="mb-3">
                    <i className="fas fa-chart-bar feature-icon"></i>
                  </div>
                  <h5 className="card-title">Controle Financeiro</h5>
                  <p className="card-text text-muted">
                    Relatórios financeiros completos com valores recebidos, pendentes e estatísticas. Acompanhe a saúde financeira do seu consultório.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Card 6: Integrações e Segurança */}
            <div className="col-lg-4 col-md-6">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body text-center p-4">
                  <div className="mb-3">
                    <i className="fas fa-shield-alt feature-icon"></i>
                  </div>
                  <h5 className="card-title">Integrações e Segurança</h5>
                  <p className="card-text text-muted">
                    Sincronização com <strong>Google Calendar e Gmail</strong>. Criptografia de dados sensíveis em conformidade com LGPD.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-5 bg-light">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold section-title">Por que escolher o Sophya?</h2>
            <p className="text-muted">Vantagens que fazem a diferença no seu dia a dia</p>
          </div>
          
          <div className="row g-4">
            <div className="col-lg-6">
              <div className="benefit-item">
                <div className="benefit-icon text-white rounded-circle d-flex align-items-center justify-content-center" style={{ width: '60px', height: '60px' }}>
                  <i className="fas fa-lock"></i>
                </div>
                <div className="benefit-content">
                  <h5>Segurança e Privacidade</h5>
                  <p className="text-muted">
                    Dados protegidos com criptografia e controle de acesso por empresa
                  </p>
                </div>
              </div>
            </div>
            
            <div className="col-lg-6">
              <div className="benefit-item">
                <div className="benefit-icon text-white rounded-circle d-flex align-items-center justify-content-center" style={{ width: '60px', height: '60px' }}>
                  <i className="fas fa-cloud"></i>
                </div>
                <div className="benefit-content">
                  <h5>Acesso na Nuvem</h5>
                  <p className="text-muted">
                    Acesse de qualquer lugar, a qualquer hora, em qualquer dispositivo
                  </p>
                </div>
              </div>
            </div>
            
            <div className="col-lg-6">
              <div className="benefit-item">
                <div className="benefit-icon text-white rounded-circle d-flex align-items-center justify-content-center" style={{ width: '60px', height: '60px' }}>
                  <i className="fas fa-mobile-alt"></i>
                </div>
                <div className="benefit-content">
                  <h5>Design Responsivo</h5>
                  <p className="text-muted">
                    Interface adaptada para computadores, tablets e smartphones
                  </p>
                </div>
              </div>
            </div>
            
            <div className="col-lg-6">
              <div className="benefit-item">
                <div className="benefit-icon text-white rounded-circle d-flex align-items-center justify-content-center" style={{ width: '60px', height: '60px' }}>
                  <i className="fas fa-headset"></i>
                </div>
                <div className="benefit-content">
                  <h5>Suporte Especializado</h5>
                  <p className="text-muted">
                    Equipe técnica especializada em sistemas para profissionais de saúde
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section id="security" className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold section-title">Segurança e Conformidade Legal</h2>
            <p className="text-muted">Seu consultório e seus pacientes protegidos conforme as normas brasileiras</p>
          </div>
          
          <div className="row g-4 mb-5">
            {/* LGPD Compliance */}
            <div className="col-lg-3 col-md-6">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body text-center p-4">
                  <div className="mb-3" style={{ height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className="fas fa-shield-alt text-primary" style={{ fontSize: '3rem' }}></i>
                  </div>
                  <h5 className="card-title fw-bold mb-3" style={{ minHeight: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    Conforme LGPD
                  </h5>
                  <p className="card-text text-muted mb-3">
                    Sistema 100% adequado à Lei Geral de Proteção de Dados (Lei nº 13.709/2018). 
                    Dados dos seus pacientes protegidos com tecnologia de ponta.
                  </p>
                  <ul className="list-unstyled text-start mb-0">
                    <li className="mb-2">
                      <i className="fas fa-check text-success me-2"></i>
                      <small>Criptografia de dados sensíveis</small>
                    </li>
                    <li className="mb-2">
                      <i className="fas fa-check text-success me-2"></i>
                      <small>Controle de acesso por usuário</small>
                    </li>
                    <li className="mb-2">
                      <i className="fas fa-check text-success me-2"></i>
                      <small>Auditoria de acessos aos dados</small>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* CFP/CRP Compliance */}
            <div className="col-lg-3 col-md-6">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body text-center p-4">
                  <div className="mb-3" style={{ height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className="fas fa-user-shield text-primary" style={{ fontSize: '3rem' }}></i>
                  </div>
                  <h5 className="card-title fw-bold mb-3" style={{ minHeight: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    Resoluções CFP/CRP
                  </h5>
                  <p className="card-text text-muted mb-3">
                    Atende às resoluções do Conselho Federal de Psicologia para sigilo profissional 
                    e prontuários digitais.
                  </p>
                  <ul className="list-unstyled text-start mb-0">
                    <li className="mb-2">
                      <i className="fas fa-check text-success me-2"></i>
                      <small>Resolução CFP 001/2009 (prontuário)</small>
                    </li>
                    <li className="mb-2">
                      <i className="fas fa-check text-success me-2"></i>
                      <small>Resolução CFP 011/2018 (atendimento online)</small>
                    </li>
                    <li className="mb-2">
                      <i className="fas fa-check text-success me-2"></i>
                      <small>Sigilo absoluto de prontuários por 20 anos</small>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* ANPD Compliance */}
            <div className="col-lg-3 col-md-6">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body text-center p-4">
                  <div className="mb-3" style={{ height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className="fas fa-certificate text-primary" style={{ fontSize: '3rem' }}></i>
                  </div>
                  <h5 className="card-title fw-bold mb-3" style={{ minHeight: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    Orientações ANPD
                  </h5>
                  <p className="card-text text-muted mb-3">
                    Seguimos todas as diretrizes da Autoridade Nacional de Proteção de Dados 
                    para garantir a segurança das informações.
                  </p>
                  <ul className="list-unstyled text-start mb-0">
                    <li className="mb-2">
                      <i className="fas fa-check text-success me-2"></i>
                      <small>Algoritmos de criptografia robustos</small>
                    </li>
                    <li className="mb-2">
                      <i className="fas fa-check text-success me-2"></i>
                      <small>Gestão segura de chaves criptográficas</small>
                    </li>
                    <li className="mb-2">
                      <i className="fas fa-check text-success me-2"></i>
                      <small>Logs de auditoria completos e imutáveis</small>
                    </li>
                    <li className="mb-2">
                      <i className="fas fa-check text-success me-2"></i>
                      <small>Procedimento de resposta a incidentes</small>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Oracle Cloud Infrastructure */}
            <div className="col-lg-3 col-md-6">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body text-center p-4">
                  <div className="mb-3" style={{ height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <img 
                      src="/img/oracle-cloud-logo.png" 
                      alt="Oracle Cloud" 
                      style={{ maxWidth: '140px', maxHeight: '60px', height: 'auto', width: 'auto' }}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                      }}
                    />
                  </div>
                  <h5 className="card-title fw-bold mb-3" style={{ minHeight: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    Hospedado na Oracle Cloud
                  </h5>
                  <p className="card-text text-muted mb-3">
                    Infraestrutura de nível empresarial com segurança de classe mundial e conformidade internacional.
                  </p>
                  <ul className="list-unstyled text-start mb-0">
                    <li className="mb-2">
                      <i className="fas fa-check text-success me-2"></i>
                      <small>Datacenter certificado ISO 27001</small>
                    </li>
                    <li className="mb-2">
                      <i className="fas fa-check text-success me-2"></i>
                      <small>Backup automático diário</small>
                    </li>
                    <li className="mb-2">
                      <i className="fas fa-check text-success me-2"></i>
                      <small>Disponibilidade de 99.9%</small>
                    </li>
                    <li className="mb-2">
                      <i className="fas fa-check text-success me-2"></i>
                      <small>Monitoramento 24/7</small>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Security Features Details */}
          <div className="row g-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-body p-5">
                  <h4 className="text-center mb-4 fw-bold">O que protegemos para você</h4>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <div className="d-flex align-items-start">
                        <i className="fas fa-lock text-primary me-3 mt-1"></i>
                        <div>
                          <h6 className="fw-bold mb-1">Dados Clínicos Criptografados</h6>
                          <small className="text-muted">
                            Prontuários, queixas, sintomas, diagnósticos e todas as informações sensíveis 
                            são protegidas com criptografia de nível militar.
                          </small>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <div className="d-flex align-items-start">
                        <i className="fas fa-key text-primary me-3 mt-1"></i>
                        <div>
                          <h6 className="fw-bold mb-1">Controle de Acesso</h6>
                          <small className="text-muted">
                            Cada usuário acessa apenas os dados de sua própria empresa/consultório. 
                            Isolamento total entre diferentes profissionais.
                          </small>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <div className="d-flex align-items-start">
                        <i className="fas fa-history text-primary me-3 mt-1"></i>
                        <div>
                          <h6 className="fw-bold mb-1">Auditoria Completa</h6>
                          <small className="text-muted">
                            Registro de todos os acessos aos dados sensíveis, permitindo rastreabilidade 
                            total em caso de auditoria.
                          </small>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <div className="d-flex align-items-start">
                        <i className="fas fa-database text-primary me-3 mt-1"></i>
                        <div>
                          <h6 className="fw-bold mb-1">Backup Seguro</h6>
                          <small className="text-muted">
                            Backups automáticos diários com criptografia. Seus dados nunca serão perdidos 
                            e sempre estarão protegidos.
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="text-center mt-4">
                    <Link to="/politica-seguranca" className="btn btn-outline-primary">
                      <i className="fas fa-file-shield me-2"></i>
                      Ler Política de Segurança Completa
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>


        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-5 bg-light">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold section-title">Planos e Preços</h2>
            <p className="text-muted">Escolha o plano ideal para o seu consultório</p>
          </div>
          
          <div className="row g-4">
            {/* Student Plan - Left */}
            <div className="col-lg-4 col-md-6">
              <div className="card pricing-card shadow-lg h-100">
                <div className="card-header text-white text-center py-4" style={{ background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)' }}>
                  <h3 className="mb-0">Plano Estudante</h3>
                  <small>Exclusivo para graduandos</small>
                </div>
                <div className="card-body text-center py-5 d-flex flex-column">
                  <span className="badge bg-success-subtle text-success fw-semibold align-self-center mb-3" style={{ borderRadius: '999px', padding: '0.35rem 0.75rem' }}>
                    Durante o período de formação
                  </span>
                  <div className="display-6 fw-bold text-success mb-2">
                    GRATUITO
                  </div>
                  <p className="text-muted mb-4 px-2">
                    Exclusivo para estudantes de Psicologia com comprovação de matrícula ativa.
                  </p>
                  <ul className="list-unstyled text-start mb-4">
                    <li className="mb-2">
                      <i className="fas fa-check text-success me-2"></i>
                      Funcionalidades essenciais do sistema
                    </li>
                    <li className="mb-2">
                      <i className="fas fa-check text-success me-2"></i>
                      Agendamento e gestão de pacientes
                    </li>
                    <li className="mb-2">
                      <i className="fas fa-check text-success me-2"></i>
                      Prontuários e modelos básicos
                    </li>
                    <li className="mb-2">
                      <i className="fas fa-check text-success me-2"></i>
                      Integração Google Calendar
                    </li>
                    <li className="mb-2">
                      <i className="fas fa-check text-success me-2"></i>
                      Jogos educativos e buscador de livros
                    </li>
                    <li className="mb-2">
                      <i className="fas fa-check text-success me-2"></i>
                      Suporte via comunidade
                    </li>
                  </ul>
                  <Link 
                    to="/cadastro-estudante" 
                    className="btn btn-lg w-100 mt-auto" 
                    style={{ background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)', color: 'white', border: 'none' }}
                    onClick={() => trackingService.trackClickTrialButton('Plano Estudante')}
                  >
                    Cadastrar como Estudante
                  </Link>
                  <div className="mt-3">
                    <small className="text-muted">Requer comprovante de matrícula válido.</small>
                  </div>
                </div>
              </div>
            </div>

            {/* Individual Plan - Center */}
            <div className="col-lg-4 col-md-6">
              <div className="card pricing-card shadow-lg h-100 pricing-featured">
                <div className="card-header text-white text-center py-4" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                  <h3 className="mb-0">Plano Individual</h3>
                  <small>Para psicólogos autônomos</small>
                </div>
                <div className="card-body text-center py-5 d-flex flex-column">
                  <span className="badge bg-primary-subtle text-primary fw-semibold align-self-center mb-3" style={{ borderRadius: '999px', padding: '0.35rem 0.75rem' }}>
                    Acesso completo
                  </span>
                  <div className="mb-2">
                    <span className="display-5 fw-bold text-primary">R$ 39,99</span>
                    <span className="text-muted">/mês</span>
                  </div>
                  <div className="mb-3">
                    <span className="badge bg-success-subtle text-success" style={{ fontSize: '0.85rem', padding: '0.4rem 0.8rem' }}>
                      ou R$ 399,00/ano <small>(economize 2 meses!)</small>
                    </span>
                  </div>
                  <p className="text-muted mb-4 px-2">
                    Todas as funcionalidades para gerenciar seu consultório com eficiência.
                  </p>
                  
                  <ul className="list-unstyled text-start mb-4">
                    <li className="mb-2">
                      <i className="fas fa-check text-success me-2"></i>
                      Pacientes ilimitados
                    </li>
                    <li className="mb-2">
                      <i className="fas fa-check text-success me-2"></i>
                      Agendamento com confirmação automática
                    </li>
                    <li className="mb-2">
                      <i className="fas fa-check text-success me-2"></i>
                      Prontuários com OCR (transcrição)
                    </li>
                    <li className="mb-2">
                      <i className="fas fa-check text-success me-2"></i>
                      Assistente com IA para análise
                    </li>
                    <li className="mb-2">
                      <i className="fas fa-check text-success me-2"></i>
                      Jogos educativos e buscador de livros
                    </li>
                    <li className="mb-2">
                      <i className="fas fa-check text-success me-2"></i>
                      Integrações Google Calendar e Gmail
                    </li>
                    <li className="mb-2">
                      <i className="fas fa-check text-success me-2"></i>
                      Relatórios financeiros completos
                    </li>
                    <li className="mb-2">
                      <i className="fas fa-check text-success me-2"></i>
                      Suporte técnico prioritário
                    </li>
                  </ul>
                  
                  <Link 
                    to="/cadastro" 
                    className="btn btn-primary btn-lg w-100 mt-auto"
                    onClick={() => trackingService.trackClickTrialButton('Plano Individual')}
                  >
                    Testar Grátis por 30 dias
                  </Link>
                  
                  <div className="mt-3">
                    <small className="text-muted">
                      ✅ Sem cartão de crédito • Cancele quando quiser
                    </small>
                  </div>
                </div>
              </div>
            </div>

            {/* Clinics Contact - Right */}
            <div className="col-lg-4 col-md-6">
              <div className="card pricing-card shadow-lg h-100">
                <div className="card-header text-white text-center py-4" style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)' }}>
                  <h3 className="mb-0">Clínicas e Equipes</h3>
                  <small>Solução personalizada</small>
                </div>
                <div className="card-body text-center py-5 d-flex flex-column">
                  <span className="badge bg-info-subtle text-info fw-semibold align-self-center mb-3" style={{ borderRadius: '999px', padding: '0.35rem 0.75rem' }}>
                    Sob consulta
                  </span>
                  <div className="fw-bold text-info mb-2" style={{ fontSize: '1.4rem' }}>
                    Plano Personalizado
                  </div>
                  <p className="text-muted mb-4 px-2">
                    Implantação com treinamento, suporte dedicado e todos os recursos avançados para sua clínica.
                  </p>
                  <ul className="list-unstyled text-start mb-4">
                    <li className="mb-2">
                      <i className="fas fa-check text-success me-2"></i>
                      Múltiplos profissionais e agendas
                    </li>
                    <li className="mb-2">
                      <i className="fas fa-check text-success me-2"></i>
                      Permissões e perfis de acesso
                    </li>
                    <li className="mb-2">
                      <i className="fas fa-check text-success me-2"></i>
                      Todos os recursos do plano Individual
                    </li>
                    <li className="mb-2">
                      <i className="fas fa-check text-success me-2"></i>
                      Relatórios consolidados
                    </li>
                    <li className="mb-2">
                      <i className="fas fa-check text-success me-2"></i>
                      Treinamento e onboarding
                    </li>
                    <li className="mb-2">
                      <i className="fas fa-check text-success me-2"></i>
                      Suporte dedicado
                    </li>
                  </ul>
                  <a
                    href="https://api.whatsapp.com/send?phone=5511936189517&text=Ol%C3%A1!%20Sou%20de%20uma%20cl%C3%ADnica%20e%20gostaria%20de%20saber%20mais%20sobre%20o%20plano%20para%20cl%C3%ADnicas%20do%20Sophya."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary btn-lg w-100 mt-auto"
                    aria-label="Falar com especialista no WhatsApp"
                  >
                    <i className="fab fa-whatsapp me-2"></i>
                    Falar com Especialista no WhatsApp
                  </a>
                  <div className="mt-3">
                    <small className="text-muted">Atendimento comercial das 9h às 18h (dias úteis).</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-5 bg-dark text-white">
        <div className="container">
          <div className="row g-4">
            {/* Coluna 1: Logo e Descrição */}
            <div className="col-lg-3 col-md-6">
              <div className="d-flex align-items-center mb-3">
                <img 
                  src="/img/logo-icone.png" 
                  alt="Sophya" 
                  className="me-2"
                  style={{ height: '30px' }}
                />
                <span className="fw-bold fs-5">Sophya</span>
              </div>
              <p className="text-muted mb-3">
                Sistema completo para gerenciamento de consultórios de psicologia.
              </p>
              <p className="text-muted mb-0">
                <small>© 2025 Sophya.<br/>Todos os direitos reservados.</small>
              </p>
            </div>

            {/* Coluna 2: Contato */}
            <div className="col-lg-3 col-md-6">
              <h6 className="text-white mb-3 fw-bold">Contato</h6>
              <ul className="list-unstyled">
                <li className="mb-3">
                  <a 
                    href="mailto:contato@dhtech.com.br"
                    className="text-muted text-decoration-none d-flex align-items-start"
                    style={{ transition: 'color 0.2s' }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#667eea'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#6c757d'}
                  >
                    <i className="fas fa-envelope me-2 mt-1"></i>
                    <span>contato@dhtech.com.br</span>
                  </a>
                </li>
                <li className="mb-3">
                  <a 
                    href="https://api.whatsapp.com/send?phone=5511936189517"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted text-decoration-none d-flex align-items-start"
                    style={{ transition: 'color 0.2s' }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#667eea'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#6c757d'}
                  >
                    <i className="fab fa-whatsapp me-2 mt-1"></i>
                    <span>+55 11 93618-9517</span>
                  </a>
                </li>
                <li>
                  <small className="text-muted">
                    Atendimento: Seg-Sex, 9h às 18h
                  </small>
                </li>
              </ul>
            </div>

            {/* Coluna 3: Links Legais */}
            <div className="col-lg-3 col-md-6">
              <h6 className="text-white mb-3 fw-bold">Legal</h6>
              <ul className="list-unstyled">
                <li className="mb-2">
                  <Link
                    to="/politica-seguranca"
                    className="text-muted text-decoration-none"
                    style={{ transition: 'color 0.2s' }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#667eea'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#6c757d'}
                  >
                    <i className="fas fa-shield-alt me-2"></i>
                    Política de Segurança
                  </Link>
                </li>
                <li className="mb-2">
                  <a 
                    href="/docs/politica-privacidade.html" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-muted text-decoration-none"
                    style={{ transition: 'color 0.2s' }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#667eea'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#6c757d'}
                  >
                    <i className="fas fa-user-shield me-2"></i>
                    Política de Privacidade
                  </a>
                </li>
                <li className="mb-2">
                  <a 
                    href="/docs/termos-servico.html" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-muted text-decoration-none"
                    style={{ transition: 'color 0.2s' }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#667eea'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#6c757d'}
                  >
                    <i className="fas fa-file-contract me-2"></i>
                    Termos de Serviço
                  </a>
                </li>
              </ul>
            </div>

            {/* Coluna 4: Conformidade */}
            <div className="col-lg-3 col-md-6">
              <h6 className="text-white mb-3 fw-bold">Conformidade</h6>
              <div className="d-flex flex-wrap gap-2 mb-3">
                <span className="badge bg-primary-subtle text-primary px-2 py-1" style={{ fontSize: '0.75rem' }}>
                  <i className="fas fa-check-circle me-1"></i> LGPD
                </span>
                <span className="badge bg-primary-subtle text-primary px-2 py-1" style={{ fontSize: '0.75rem' }}>
                  <i className="fas fa-check-circle me-1"></i> CFP/CRP
                </span>
                <span className="badge bg-primary-subtle text-primary px-2 py-1" style={{ fontSize: '0.75rem' }}>
                  <i className="fas fa-check-circle me-1"></i> ANPD
                </span>
                <span className="badge bg-primary-subtle text-primary px-2 py-1" style={{ fontSize: '0.75rem' }}>
                  <i className="fas fa-check-circle me-1"></i> ISO 27001
                </span>
              </div>
              <div className="mt-3">
                <img 
                  src="/img/oracle-cloud-logo.png" 
                  alt="Hospedado na Oracle" 
                  style={{ maxWidth: '120px', height: 'auto', opacity: 0.7 }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
              </div>
              <p className="text-muted mt-2 mb-0">
                <small>Versão 1.0.0 Beta</small>
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Botão WhatsApp Flutuante */}
      <a
        href="https://api.whatsapp.com/send?phone=5511936189517&text=Olá!%20Gostaria%20de%20saber%20mais%20sobre%20o%20Sistema%20Sophya"
        target="_blank"
        rel="noopener noreferrer"
        className="whatsapp-float"
        title="Fale conosco no WhatsApp"
        aria-label="Contato via WhatsApp"
        onClick={() => trackingService.trackWhatsAppClick('botão flutuante')}
      >
        <i className="fab fa-whatsapp"></i>
      </a>

      {/* Popup de Promoção - Ativado via PROMO_CONFIG */}
      {PROMO_CONFIG.enabled && showPromoPopup && (
        <div 
          className="blackfriday-overlay" 
          onClick={closePromoPopup}
          onKeyDown={(e) => {
            if (e.key === 'Escape') closePromoPopup();
          }}
          role="dialog"
          aria-modal="true"
          tabIndex={0}
        >
          <div className="blackfriday-popup" onClick={(e) => e.stopPropagation()}>
            <button 
              className="blackfriday-close" 
              onClick={closePromoPopup}
              aria-label="Fechar popup"
            >
              <i className="fas fa-times"></i>
            </button>
            
            <div className="blackfriday-content">
              <div className="blackfriday-badge">
                <i className="fas fa-bolt me-2"></i>
                {PROMO_CONFIG.name}
              </div>
              
              <h2 className="blackfriday-title">
                🔥 Oferta Exclusiva!
              </h2>
              
              <div className="blackfriday-dates">
                <i className="fas fa-calendar-alt me-2"></i>
                {PROMO_CONFIG.startDate.getDate()} a {PROMO_CONFIG.endDate.getDate()} de Novembro
              </div>
              
              <div className="blackfriday-benefits">
                <div className="blackfriday-benefit">
                  <div className="benefit-icon-bf">
                    <i className="fas fa-gift"></i>
                  </div>
                  <div className="benefit-text-bf">
                    <strong>{PROMO_CONFIG.trialDays} dias de teste grátis</strong>
                    <span>Para todos os novos cadastros</span>
                  </div>
                </div>
                
                <div className="blackfriday-divider">+</div>
                
                <div className="blackfriday-benefit">
                  <div className="benefit-icon-bf">
                    <i className="fas fa-ticket-alt"></i>
                  </div>
                  <div className="benefit-text-bf">
                    <strong>Cupom de {PROMO_CONFIG.discountPercent}% OFF</strong>
                    <span>Válido para o plano anual (use quando quiser)</span>
                  </div>
                </div>
              </div>
              
              <div className="blackfriday-cta">
                <Link 
                  to="/cadastro" 
                  className="btn btn-blackfriday btn-lg"
                  onClick={closePromoPopup}
                >
                  <i className="fas fa-rocket me-2"></i>
                  Garantir Minha Oferta
                </Link>
              </div>
              
              <div className="blackfriday-footer">
                <small>
                  <i className="fas fa-clock me-1"></i>
                  Oferta válida apenas de {PROMO_CONFIG.startDate.getDate()} a {PROMO_CONFIG.endDate.getDate()}/11/{PROMO_CONFIG.startDate.getFullYear()}
                </small>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;